import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, getDoc, setDoc, updateDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';

export const useFirestoreBalance = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setBalance(null);
      return;
    }

    // Use the same document path as the balance deduction code
    // Path: users/{userId}/balance/current
    // Field: amount
    const balanceRef = doc(db, 'users', user.uid, 'balance', 'current');
    
    const unsubscribe = onSnapshot(balanceRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Use 'amount' field to match the balance deduction code
        setBalance(data.amount || 0);
        setError(null);
      } else {
        // Handle case where user has no balance document yet
        setBalance(0);
      }
      setLoading(false);
    }, (err) => {
      console.error("Error fetching balance:", err);
      setError("Failed to load balance.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Deduct balance function
  const deductBalance = useCallback(async (amount: number, description: string): Promise<void> => {
    if (!user || amount <= 0) {
      throw new Error('Invalid user or amount');
    }

    try {
      const balanceRef = doc(db, 'users', user.uid, 'balance', 'current');
      const balanceDoc = await getDoc(balanceRef);
      
      let currentBalance = 0;
      if (balanceDoc.exists()) {
        currentBalance = balanceDoc.data().amount || 0;
      } else {
        // Create balance document if it doesn't exist
        await setDoc(balanceRef, {
          amount: 0,
          status: 'Active',
          lastUpdated: Timestamp.now(),
          createdAt: Timestamp.now()
        });
      }
      
      const newBalance = Math.max(0, currentBalance - amount); // Prevent negative balance
      
      await updateDoc(balanceRef, {
        amount: newBalance,
        lastUpdated: Timestamp.now()
      });
      
      // Create billing transaction record
      await addDoc(collection(db, 'users', user.uid, 'billing'), {
        type: 'debit',
        amount: amount,
        description: description,
        status: 'paid',
        date: Timestamp.now(),
        createdAt: Timestamp.now()
      });
      
      console.log(`💰 Deducted ₹${amount.toFixed(2)} from balance. New balance: ₹${newBalance.toFixed(2)}`);
    } catch (error) {
      console.error('Error deducting balance:', error);
      throw error;
    }
  }, [user]);

  return { balance, loading, error, deductBalance };
};
